import { Box, Skeleton } from '@mui/material';
import React, { useState } from 'react';
import { sectionGap, stackedClassName } from './mdConsts';

interface IMDImgProps {
	src: string;
	alt: string;
}

const EXTENSION = /(\.[a-z0-9]+)$/i;
const FADE = '400ms ease';

// The static repo publishes foo.lowres.jpeg (32px) next to foo.jpeg.
export const toLowresSrc = (src: string) =>
	EXTENSION.test(src) ? src.replace(EXTENSION, '.lowres$1') : null;

const MDImg: React.FC<IMDImgProps> = ({ src, alt }) => {
	const lowresSrc = toLowresSrc(src);
	// Keyed by src: the renderer can swap markdown without remounting the image.
	const [settledSrc, setSettledSrc] = useState<string>();
	const [failedLowresSrc, setFailedLowresSrc] = useState<string | null>(null);
	const fullLoaded = settledSrc === src;
	const hasPreview = !!lowresSrc && failedLowresSrc !== lowresSrc;
	const settle = () => setSettledSrc(src);

	return (
		<Box
			data-testid="md-img-container"
			sx={{
				position: 'relative',
				overflow: 'hidden',
				borderRadius: 2,
				[`:not(.${stackedClassName} > &)`]: {
					mt: sectionGap
				}
			}}
		>
			{hasPreview && (
				// The 32px preview's rounded aspect ratio is off by a few pixels, so it
				// covers the full image's box instead of sizing it.
				<Box
					data-testid="md-img-preview"
					component="img"
					src={lowresSrc}
					alt=""
					aria-hidden
					onError={() => setFailedLowresSrc(lowresSrc)}
					sx={{
						position: 'absolute',
						inset: 0,
						width: '100%',
						height: '100%',
						objectFit: 'cover',
						filter: 'blur(8px)',
						transform: 'scale(1.05)',
						opacity: fullLoaded ? 0 : 1,
						transition: `opacity ${FADE} 400ms`
					}}
				/>
			)}
			{hasPreview && (
				<Skeleton
					data-testid="md-img-shimmer"
					variant="rectangular"
					animation="wave"
					sx={{
						position: 'absolute',
						inset: 0,
						width: '100%',
						height: '100%',
						bgcolor: 'transparent',
						opacity: fullLoaded ? 0 : 1,
						transition: `opacity ${FADE}`,
						'&::after': {
							background:
								'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)'
						}
					}}
				/>
			)}
			<Box
				data-testid="md-img"
				component="img"
				src={src}
				alt={alt}
				onLoad={settle}
				onError={settle}
				sx={{
					display: 'block',
					position: 'relative',
					width: '100%',
					opacity: hasPreview && !fullLoaded ? 0 : 1,
					transition: `opacity ${FADE}`
				}}
			/>
		</Box>
	);
};

export default MDImg;
